import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, IconButton } from '@mui/material';
import {
  BatteryFull as BatteryIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const BatteryOptionsSection = ({
  showBatteryOptions,
  toggleBatteryOptions,
  batteryCapacity,
  setBatteryCapacity,
  departureSoC,
  setDepartureSoC,
  arrivalSoC,
  setArrivalSoC,
  vehicleRange
}) => {
  // Calculate battery summary
  const currentEnergy = (batteryCapacity * departureSoC) / 100;
  const availableEnergy = (batteryCapacity * (departureSoC - arrivalSoC)) / 100;
  const estimatedRange = (availableEnergy / batteryCapacity) * vehicleRange;

  const sliderStyles = `
    input[type='range']::-webkit-slider-thumb {
      appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #22c55e;
      border: 2px solid #22c55e;
      box-shadow: 0 2px 6px rgba(34, 197, 94, 0.2);
      cursor: pointer;
    }
    input[type='range']::-webkit-slider-thumb:focus {
      outline: none;
      box-shadow: 0 0 0 4px #bbf7d0;
    }
    input[type='range']::-moz-range-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #22c55e;
      border: 2px solid #22c55e;
      box-shadow: 0 2px 6px rgba(34, 197, 94, 0.2);
      cursor: pointer;
    }
    input[type='range']::-ms-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #22c55e;
      border: 2px solid #22c55e;
      box-shadow: 0 2px 6px rgba(34, 197, 94, 0.2);
    }
  `;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <style>{sliderStyles}</style>
      
      {/* Section Header */}
      <motion.button
        type="button"
        onClick={toggleBatteryOptions}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderRadius: 8,
          backgroundColor: 'white',
          border: '1px solid #bbf7d0',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#f0fdf4';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'white';
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <BatteryIcon sx={{ color: '#16a34a', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight="medium" sx={{ color: '#1e293b' }}>
            Battery Options
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {showBatteryOptions && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleBatteryOptions();
              }}
              sx={{
                padding: '4px',
                borderRadius: '50%',
                '&:hover': { backgroundColor: '#fef2f2' },
                color: '#ef4444'
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          <motion.div
            animate={{ rotate: showBatteryOptions ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDownIcon sx={{ color: '#6b7280', fontSize: 20 }} />
          </motion.div>
        </Box>
      </motion.button>

      {/* Section Content */}
      <AnimatePresence>
        {showBatteryOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                padding: 4,
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #dcfce7',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              {/* Battery Capacity */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium" sx={{ color: '#374151' }}>
                    Battery Capacity
                  </Typography>
                  <Typography variant="body2" fontWeight="semibold" sx={{ color: '#16a34a' }}>
                    {batteryCapacity} kWh
                  </Typography>
                </Box>
                <input
                  type="range"
                  min="30"
                  max="150"
                  step="5"
                  value={batteryCapacity}
                  onChange={(e) => setBatteryCapacity(Number(e.target.value))}
                  style={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#e5e7eb',
                    borderRadius: 4,
                    appearance: 'none',
                    cursor: 'pointer'
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                  <span>30 kWh</span>
                  <span>150 kWh</span>
                </Box>
              </Box>

              {/* Current Charge */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium" sx={{ color: '#374151' }}>
                    Current Charge
                  </Typography>
                  <Typography variant="body2" fontWeight="semibold" sx={{ color: '#16a34a' }}>
                    {departureSoC}%
                  </Typography>
                </Box>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={departureSoC}
                  onChange={(e) => setDepartureSoC(Number(e.target.value))}
                  style={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#e5e7eb',
                    borderRadius: 4,
                    appearance: 'none',
                    cursor: 'pointer'
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                  <span>10%</span>
                  <span>100%</span>
                </Box>
              </Box>

              {/* Minimum Charge at Destination */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium" sx={{ color: '#374151' }}>
                    Minimum Charge at Destination
                  </Typography>
                  <Typography variant="body2" fontWeight="semibold" sx={{ color: '#16a34a' }}>
                    {arrivalSoC}%
                  </Typography>
                </Box>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={arrivalSoC}
                  onChange={(e) => setArrivalSoC(Number(e.target.value))}
                  style={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#e5e7eb',
                    borderRadius: 4,
                    appearance: 'none',
                    cursor: 'pointer'
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                  <span>5%</span>
                  <span>50%</span>
                </Box>
              </Box>

              {/* Battery Summary */}
              <Box
                sx={{
                  padding: 3,
                  backgroundColor: '#f0fdf4',
                  borderRadius: 2,
                  border: '1px solid #bbf7d0'
                }}
              >
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#374151', mb: 2 }}>
                  Battery Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>Total Capacity:</Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ color: '#1f2937' }}>
                      {batteryCapacity} kWh
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>Current Energy:</Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ color: '#1f2937' }}>
                      {currentEnergy.toFixed(1)} kWh
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>Available for Trip:</Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ color: '#1f2937' }}>
                      {availableEnergy.toFixed(1)} kWh
                    </Typography>
                  </Box>
                  <Box sx={{ borderTop: '1px solid #bbf7d0', pt: 1, mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="semibold" sx={{ color: '#374151' }}>
                        Estimated Range:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#16a34a' }}>
                        {estimatedRange.toFixed(0)} km
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Battery Visual Indicator */}
              <Box
                sx={{
                  padding: 3,
                  backgroundColor: '#f0fdf4',
                  borderRadius: 2,
                  border: '1px solid #bbf7d0'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 64,
                      height: 32,
                      border: '2px solid #bbf7d0',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease',
                        width: `${departureSoC}%`
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: -2,
                        width: 4,
                        height: 16,
                        backgroundColor: '#6b7280',
                        borderRadius: '0 2px 2px 0',
                        transform: 'translateY(-50%)'
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="medium" sx={{ color: '#1f2937' }}>
                      {departureSoC}% → {arrivalSoC}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      Using {(departureSoC - arrivalSoC)}% for trip
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default BatteryOptionsSection;
